import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
	BookOpen,
	Castle,
	Check,
	ChefHat,
	ChevronDown,
	Globe2,
	Home,
	Loader2,
	Map as MapIcon,
	MapPin,
	Mountain,
	Package,
	Scroll,
	ScrollText,
	Sparkles,
	Store,
	Swords,
	Tent,
	Trophy,
	Upload,
	User,
	Users,
	type LucideIcon,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CompTag } from '@/components/CompTag';
import { ContentTypeTile } from '@/components/ContentTypeTile';
import { Countdown } from '@/components/Countdown';
import { ImgPlaceholder } from '@/components/ImgPlaceholder';
import { LoomliteMark } from '@/components/LoomliteMark';
import { useAuth } from '@/contexts/AuthContext';
import { useCompetition } from '@/hooks/useCompetitions';
import { fetchEntitySummaries, type EntitySummary } from '@/lib/entitySummaries';
import { supabase } from '@/lib/supabase';
import { formatDate } from '@/lib/utils';
import { resolveGrandPrize } from '@/lib/grandPrize';
import { extractSubmissionId } from '@/lib/snapshotResponse';
import { COMMUNITY_GUIDELINES_URL, COMPETITION_RULES_URL } from '@/lib/links';
import {
	LOCATION_SUB_TABLES,
	LOCATION_SUB_TABLE_META,
	LOCATION_VIRTUAL_TYPE,
	type LocationSubTable,
} from '@/lib/locationTypes';

interface TypeMeta {
	icon: LucideIcon;
	label: string;
	description: string;
}

/**
 * Presentation for each content type a competition can accept.
 *
 * The authoritative list is the seed of `CompetitionSnapshotDefaults`
 * (Lantern 20260420000001 §10), which is also what Loomlite Admin offers when
 * an editor picks the allowed types. Anything missing here still submits — a
 * type with no entry falls back to a generic tile via `metaFor` — it just
 * arrives unlabelled, which is how eight of the ten used to look.
 *
 * `WrittenEntries` / `Written` were listed here and are not tables at all, so
 * choosing one could only ever produce a PostgREST error. Removed.
 */
const TYPE_META: Record<string, TypeMeta> = {
	Characters: { icon: User, label: 'Character', description: 'A hero, villain, NPC sheet' },
	Items: { icon: Package, label: 'Item', description: 'Weapons, armour, trinkets' },
	Skills: { icon: Sparkles, label: 'Skill', description: 'Abilities, spells, techniques' },
	Encounters: { icon: Swords, label: 'Encounter', description: 'Creatures, phases, objectives' },
	Quests: { icon: ScrollText, label: 'Quest', description: 'Milestones, rewards, hooks' },
	Stories: { icon: BookOpen, label: 'Story', description: 'Acts, arcs, milestones' },
	Shops: { icon: Store, label: 'Shop', description: 'Merchants and their stock' },
	Maps: { icon: MapIcon, label: 'Map', description: 'Battle maps, region maps' },
	Recipes: { icon: ChefHat, label: 'Recipe', description: 'Crafting and cooking formulas' },
	Location: { icon: MapPin, label: 'Location', description: 'City, town, country, continent…' },
	Campaigns: { icon: Users, label: 'Campaign', description: 'Arcs, sessions, handouts' },
};

const LOCATION_SUB_ICONS: Record<LocationSubTable, LucideIcon> = {
	Cities: Castle,
	Towns: Home,
	Villages: Tent,
	Continents: Globe2,
	Countrys: Mountain,
};

function metaFor(entityType: string): TypeMeta {
	return (
		TYPE_META[entityType] ?? { icon: Scroll, label: entityType, description: 'From your library' }
	);
}

export function SubmitWizardPage() {
	const { slug } = useParams();
	const navigate = useNavigate();
	const { user, session } = useAuth();
	const { data: competition, isLoading } = useCompetition(slug);

	const [entityType, setEntityType] = useState<string | null>(null);
	const [locationSubType, setLocationSubType] = useState<LocationSubTable>(LOCATION_SUB_TABLES[0]);
	const [entities, setEntities] = useState<EntitySummary[]>([]);
	const [entityId, setEntityId] = useState<string>('');
	const [loadingEntities, setLoadingEntities] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [rulesAgreed, setRulesAgreed] = useState(false);

	const isLocationCategory = entityType === LOCATION_VIRTUAL_TYPE;

	// The actual Postgres table to query / submit. For the virtual Location
	// category this is the chosen sub-table; otherwise the entity type itself.
	const resolvedTable = isLocationCategory ? locationSubType : entityType;

	const allowedTypes = competition?.AllowedEntityTypes ?? [];
	const closed = competition?.Status !== 'open';

	useEffect(() => {
		// Read the element first and branch on it, rather than checking .length and
		// then indexing — the length check does not narrow the index access.
		const first = allowedTypes[0];
		if (!entityType && first) setEntityType(first);
	}, [allowedTypes, entityType]);

	useEffect(() => {
		if (!resolvedTable || !user) return;
		setLoadingEntities(true);
		setEntityId('');
		fetchEntitySummaries(supabase, resolvedTable, user.id)
			.then((list) => {
				setEntities(list);
				const first = list[0];
				if (first) setEntityId(first.Id);
			})
			.catch((err: Error) => {
				setEntities([]);
				toast.error(`Could not load ${resolvedTable}: ${err.message}`);
			})
			.finally(() => setLoadingEntities(false));
	}, [resolvedTable, user]);

	const selectedEntity = useMemo(
		() => entities.find((e) => e.Id === entityId) ?? null,
		[entities, entityId],
	);

	if (!user)
		return <Navigate to="/login" state={{ from: `/competitions/${slug}/submit` }} replace />;
	if (isLoading) return <p className="p-8 text-loomlite-muted">Loading…</p>;
	if (!competition) return <Navigate to="/" replace />;

	const canSubmit = !closed && entityType && entityId && rulesAgreed && !submitting;

	const confirmSubmit = async () => {
		if (!entityType || !entityId || !session || !resolvedTable) return;
		setSubmitting(true);
		try {
			const { data, error } = await supabase.functions.invoke('competition-snapshot', {
				body: {
					competitionId: competition.Id,
					entityType: resolvedTable,
					entityId,
				},
			});
			if (error) throw error;
			if (!data?.success) throw new Error(data?.error?.message ?? 'Submission failed');
			navigate(`/competitions/${slug}/thanks`, {
				state: {
					entityType: resolvedTable,
					entityTitle: selectedEntity?.Name,
					entryId: extractSubmissionId(data),
					submittedAt: new Date().toISOString(),
				},
			});
		} catch (e) {
			toast.error((e as Error).message);
		} finally {
			setSubmitting(false);
		}
	};

	const typeMeta = entityType ? metaFor(entityType) : null;
	const prize = resolveGrandPrize(competition);

	return (
		<div className="min-h-screen">
			<header className="border-b border-loomlite-border bg-loomlite-ink/50 backdrop-blur">
				<div className="mx-auto flex max-w-6xl items-center justify-between px-7 py-3.5">
					<div className="flex items-center gap-3.5">
						<LoomliteMark size={24} />
						<span className="text-sm text-loomlite-dim">/</span>
						<Link
							to={`/competitions/${slug}`}
							className="text-sm font-medium text-loomlite-text hover:text-loomlite-purple-soft"
						>
							{competition.Name}
						</Link>
					</div>
					<div className="flex items-center gap-3">
						{closed ? (
							<CompTag color="red">Submissions closed</CompTag>
						) : (
							<>
								<span className="text-xs text-loomlite-dim">Closes in</span>
								<Countdown compact targetIso={competition.SubmissionCloseAt} />
							</>
						)}
					</div>
				</div>
			</header>

			<main className="mx-auto grid max-w-6xl grid-cols-1 gap-7 px-7 py-8 lg:grid-cols-[1fr_340px]">
				<div
					className={closed ? 'pointer-events-none opacity-60' : undefined}
					aria-disabled={closed}
				>
					<div className="mb-5">
						<h1 className="m-0 font-heading text-3xl font-semibold tracking-tight">
							Submit an entry
						</h1>
						<p className="m-0 mt-1.5 text-sm text-loomlite-muted">
							Up to {competition.MaxEntriesPerUser} entry
							{competition.MaxEntriesPerUser > 1 ? ' ' : ' '}
							per user. You can withdraw any time before the deadline.
						</p>
					</div>

					<div className="mb-6">
						<Label>Content type</Label>
						<div
							className="grid gap-2.5"
							style={{
								gridTemplateColumns: `repeat(${Math.min(4, allowedTypes.length || 4)}, minmax(0, 1fr))`,
							}}
						>
							{allowedTypes.map((t) => {
								const m = metaFor(t);
								return (
									<ContentTypeTile
										key={t}
										icon={m.icon}
										label={m.label}
										description={m.description}
										active={entityType === t}
										onClick={() => setEntityType(t)}
									/>
								);
							})}
						</div>
					</div>

					{entityType && isLocationCategory && (
						<div className="mb-4">
							<Label>Location type</Label>
							<div
								className="grid gap-2"
								style={{
									gridTemplateColumns: `repeat(${LOCATION_SUB_TABLES.length}, minmax(0, 1fr))`,
								}}
							>
								{LOCATION_SUB_TABLES.map((sub) => {
									const meta = LOCATION_SUB_TABLE_META[sub];
									return (
										<ContentTypeTile
											key={sub}
											icon={LOCATION_SUB_ICONS[sub]}
											label={meta.label}
											active={locationSubType === sub}
											compact
											onClick={() => setLocationSubType(sub)}
										/>
									);
								})}
							</div>
						</div>
					)}

					{entityType && (
						<div className="mb-4">
							<Label>
								{isLocationCategory
									? `Choose a ${LOCATION_SUB_TABLE_META[locationSubType].label.toLowerCase()}`
									: `Choose a ${typeMeta?.label.toLowerCase() ?? entityType.toLowerCase()}`}
							</Label>
							<div className="relative">
								<Select value={entityId} onChange={(e) => setEntityId(e.target.value)}>
									{loadingEntities && <option>Loading…</option>}
									{!loadingEntities && entities.length === 0 && (
										<option value="">
											No{' '}
											{isLocationCategory
												? `${LOCATION_SUB_TABLE_META[locationSubType].label.toLowerCase()} entries`
												: (typeMeta?.label.toLowerCase() ?? 'entries')}{' '}
											found
										</option>
									)}
									{entities.map((e) => (
										<option key={e.Id} value={e.Id}>
											{e.Name}
										</option>
									))}
								</Select>
								<ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-loomlite-muted" />
							</div>
							<p className="mt-1.5 text-xs text-loomlite-dim">
								Pulls from your Loomlite library. Don't see it? Create a new one on the main
								Loomlite site first.
							</p>
						</div>
					)}

					{selectedEntity && (
						<div className="mb-5 flex items-center gap-3.5 rounded-xl border border-loomlite-border bg-loomlite-card p-3.5">
							<div className="h-[72px] w-[72px] flex-shrink-0">
								<ImgPlaceholder label="cover" height={72} tone="gold" />
							</div>
							<div className="min-w-0 flex-1">
								<div className="truncate text-sm font-semibold text-loomlite-text">
									{selectedEntity.Name}
								</div>
								<div className="mt-0.5 text-xs text-loomlite-muted">
									{typeMeta?.description ?? 'From your Loomlite library'}
								</div>
								<div className="mt-1.5 flex gap-1.5">
									<CompTag color="muted" withDot={false}>
										{isLocationCategory
											? LOCATION_SUB_TABLE_META[locationSubType].label
											: (typeMeta?.label ?? entityType)}
									</CompTag>
									{competition.GameSystemFilter && (
										<CompTag color="purple" withDot={false}>
											{competition.GameSystemFilter}
										</CompTag>
									)}
								</div>
							</div>
						</div>
					)}

					<label className="mt-4 flex cursor-pointer items-start gap-2.5">
						<input
							type="checkbox"
							checked={rulesAgreed}
							onChange={(e) => setRulesAgreed(e.target.checked)}
							className="mt-0.5 h-4 w-4 accent-loomlite-purple"
							style={{ accentColor: 'var(--color-loomlite-purple)' }}
						/>
						<span className="text-sm leading-relaxed text-loomlite-muted">
							I confirm this is my own work, and I agree to the{' '}
							<a
								href={COMPETITION_RULES_URL}
								target="_blank"
								rel="noopener noreferrer"
								className="text-loomlite-purple-soft underline-offset-2 hover:underline"
							>
								competition rules
							</a>{' '}
							and{' '}
							<a
								href={COMMUNITY_GUIDELINES_URL}
								target="_blank"
								rel="noopener noreferrer"
								className="text-loomlite-purple-soft underline-offset-2 hover:underline"
							>
								community guidelines
							</a>
							. Loomlite may feature the winning entry.
						</span>
					</label>

					<div className="mt-6 flex gap-2.5">
						<Button variant="outline" disabled={submitting}>
							Save draft
						</Button>
						<Button className="ml-auto" disabled={!canSubmit} onClick={confirmSubmit}>
							{submitting ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<Upload className="h-4 w-4" />
							)}
							{closed ? 'Submissions closed' : 'Submit entry'}
						</Button>
					</div>
				</div>

				<aside className="flex flex-col gap-4">
					<div className="rounded-xl border border-loomlite-border bg-loomlite-card p-[18px]">
						<div className="mb-2.5 text-[11px] uppercase tracking-[0.1em] text-loomlite-dim">
							{closed ? 'Deadline passed' : 'Submissions close in'}
						</div>
						{closed ? (
							<div className="font-heading text-[18px] font-semibold">
								{formatDate(competition.SubmissionCloseAt)}
							</div>
						) : (
							<Countdown targetIso={competition.SubmissionCloseAt} />
						)}
						{competition.AnnouncementAt && (
							<div className="mt-3 text-xs leading-relaxed text-loomlite-muted">
								Judging window opens {formatDate(competition.SubmissionCloseAt).split(',')[0]}.
								Winner announced {formatDate(competition.AnnouncementAt).split(',')[0]}.
							</div>
						)}
					</div>

					<div className="rounded-xl border border-loomlite-gold/35 bg-loomlite-card p-[18px]">
						<div className="mb-2.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-loomlite-gold-soft">
							<Trophy className="h-4 w-4" /> Grand prize
						</div>
						<div className="text-[15px] font-semibold leading-snug text-loomlite-text">
							{prize.headline}
						</div>
						<ul className="m-0 mt-3 flex list-none flex-col gap-1.5 p-0 text-xs text-loomlite-muted">
							{prize.perks.map((perk) => (
								<li key={perk} className="flex gap-2">
									<Check
										className="mt-0.5 h-3 w-3 flex-shrink-0 text-loomlite-green"
										strokeWidth={3}
									/>
									{perk}
								</li>
							))}
						</ul>
					</div>
				</aside>
			</main>
		</div>
	);
}
